import { ActiveFilters, NpmFilteredViewResponse, projectActionDisplayLabel, SavedView } from "@app/network-portfolio-management/interfaces";

export const savedViewsQueryKey = (userId: number | undefined): readonly ["npm-saved-views", number | undefined] =>
  ["npm-saved-views", userId] as const;

export function toSavedView(r: NpmFilteredViewResponse): SavedView {
  return {
    description: r.description ?? null,
    failedFilterProperties: r.failedFilterProperties ?? [],
    filters: {
      linesOfBusinessIds: r.filters.linesOfBusinessIds ?? [],
      projectAction: r.filters.projectAction ?? null,
      projectStatuses: r.filters.projectStatuses ?? [],
      projectTypes: r.filters.projectTypes ?? [],
      projectUtilizationCategoryIds: r.filters.projectUtilizationCategoryIds ?? [],
      searchTerm: r.filters.searchTerm ?? "",
      serviceAreaCounties: (r.filters.serviceAreaCounties ?? []).map((c) => ({ county: c.county, state: c.state })),
      serviceAreaStates: r.filters.serviceAreaStates ?? [],
      specialtyGroups: (r.filters.specialtyGroups ?? []).map((s) => ({ code: s.code, name: s.name })),
      tagIds: r.filters.tagIds ?? [],
      templateNames: r.filters.templateNames ?? [],
    },
    hasFilterLoadError: r.hasFilterLoadError ?? false,
    isDefault: r.isDefault ?? false,
    isQesView: false,
    name: r.name,
    npmFilteredViewId: r.npmFilteredViewId,
  };
}

export function normalizedFiltersJson(f: ActiveFilters): string {
  return JSON.stringify({
    linesOfBusinessIds: [...f.linesOfBusinessIds].sort((a, b) => a - b),
    projectAction: f.projectAction,
    projectStatuses: [...f.projectStatuses].sort(),
    projectTypes: [...f.projectTypes].sort(),
    projectUtilizationCategoryIds: [...f.projectUtilizationCategoryIds].sort((a, b) => a - b),
    searchTerm: f.searchTerm.trim(),
    serviceAreaCounties: [...f.serviceAreaCounties].sort((a, b) => a.state.localeCompare(b.state) || a.county.localeCompare(b.county)),
    serviceAreaStates: [...f.serviceAreaStates].sort(),
    specialtyGroups: [...f.specialtyGroups].sort((a, b) => a.code.localeCompare(b.code) || a.name.localeCompare(b.name)),
    tagIds: [...f.tagIds].sort((a, b) => a - b),
    templateNames: [...f.templateNames].sort(),
  });
}

export function filterSummary(filters: ActiveFilters): string {
  if (!hasActiveFilters(filters)) return "";
  const parts: string[] = [];
  if (filters.projectTypes.length > 0) parts.push(`Project Type: ${filters.projectTypes.length}`);
  if (filters.linesOfBusinessIds.length > 0) parts.push(`LOB: ${filters.linesOfBusinessIds.length}`);
  const serviceAreaCount = filters.serviceAreaCounties.length + filters.serviceAreaStates.length;
  if (serviceAreaCount > 0) parts.push(`Service Area: ${serviceAreaCount}`);
  if (filters.specialtyGroups.length > 0) parts.push(`Specialty Group: ${filters.specialtyGroups.length}`);
  if (filters.tagIds.length > 0) parts.push(`Tags: ${filters.tagIds.length}`);
  if (filters.templateNames.length > 0) parts.push(`Template: ${filters.templateNames.length}`);
  if (filters.projectStatuses.length > 0) parts.push(`Status: ${filters.projectStatuses.length}`);
  const searchTerm = filters.searchTerm.trim();
  if (searchTerm.length > 0) parts.push(`Search: "${searchTerm}"`);
  if (filters.projectAction !== null) parts.push(`Project Action: ${projectActionDisplayLabel[filters.projectAction]}`);
  if (filters.projectUtilizationCategoryIds.length > 0) parts.push(`Project Utilization: ${filters.projectUtilizationCategoryIds.length}`);
  return parts.join(" · ");
}

export function hasActiveFilters(filters: ActiveFilters): boolean {
  return (
    filters.projectTypes.length > 0 ||
    filters.linesOfBusinessIds.length > 0 ||
    filters.serviceAreaCounties.length + filters.serviceAreaStates.length > 0 ||
    filters.specialtyGroups.length > 0 ||
    filters.tagIds.length > 0 ||
    filters.templateNames.length > 0 ||
    filters.projectStatuses.length > 0 ||
    filters.searchTerm.trim().length > 0 ||
    filters.projectAction !== null ||
    filters.projectUtilizationCategoryIds.length > 0
  );
}
